import { useRef, useEffect, useMemo } from "react";
import Highcharts from "highcharts";
import initExporting from "highcharts/modules/exporting";
import initOfflineExporting from "highcharts/modules/offline-exporting";
import HighchartsReact from "highcharts-react-official";
import { Empty, Typography } from "antd";

// Initialize exporting modules (needed for SVG→PNG capture in PDF export)
initExporting(Highcharts);
initOfflineExporting(Highcharts);
import { useSlicerVariables, useWidgetData } from "../Contexts/dashboardHooks";
import { useChartSync } from "../Contexts/ChartSyncContext";
import { WidgetInfoButton } from "../Info/WidgetInfoButton";
import { resolveTemplate } from "../Utils/resolveTemplate";

const { Text } = Typography;

const CHART_CONTAINER_STYLE: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  overflow: "hidden",
};

export function ChartWidget({ id }: { id: string }) {
  const data = useWidgetData(id);
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const syncContext = useChartSync();
  const requestSyncRef = useRef<() => void>(() => {});
  if (syncContext) requestSyncRef.current = syncContext.requestSync;

  const slicerValues = useSlicerVariables();
  const rawTitle = data?.type === "chart" ? data.spec.display.title : null;
  const chartTitle = rawTitle ? resolveTemplate(rawTitle, slicerValues).resolved : null;

  const options = useMemo(() => {
    if (!data || data.type !== "chart") return null;
    const base = {
      ...data.chart.chartOptions,
      chart: {
        ...((data.chart.chartOptions.chart as Record<string, unknown>) ?? {}),
        reflow: true,
        events: {
          load() {
            requestSyncRef.current();
          },
        },
      },
      title: { text: "" },
      exporting: {
        ...((data.chart.chartOptions.exporting as Record<string, unknown> | undefined) ?? {}),
        buttons: {
          ...(((data.chart.chartOptions.exporting as Record<string, unknown> | undefined)?.buttons as Record<string, unknown> | undefined) ?? {}),
          contextButton: {
            align: "right",
            verticalAlign: "top",
            x: 6,
            y: -4,
          },
        },
      },
    };

    // Preserve legend-toggled visibility across options updates
    const chart = chartRef.current?.chart;
    if (chart && Array.isArray(base.series)) {
      const hiddenNames = new Set(
        chart.series.filter((s: any) => !s.visible).map((s: any) => s.name),
      );
      if (hiddenNames.size > 0) {
        base.series = (base.series as any[]).map((s: any) =>
          hiddenNames.has(s.name) ? { ...s, visible: false } : s,
        );
      }
    }

    return base;
  }, [data]);

  useEffect(() => {
    if (!syncContext) return;
    const getCell = () =>
      containerRef.current?.closest(".grid-item-wrapper") ?? containerRef.current ?? null;
    const getChart = () => chartRef.current?.chart ?? null;

    syncContext.register(id, getCell, getChart);
    const t = setTimeout(() => syncContext.requestSync(), 200);

    return () => {
      clearTimeout(t);
      syncContext.unregister(id);
    };
  }, [id, syncContext]);

  const hasData = useMemo(() => {
    if (!options) return false;
    const series = options.series as Array<{ data?: unknown[] }> | undefined;
    if (!series || series.length === 0) return false;
    return series.some((s) =>
      Array.isArray(s.data) && s.data.some((v) => v !== null && v !== undefined),
    );
  }, [options]);

  if (!options) {
    return (
      <div className="chart-widget" data-grid-id={id} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No chart data" />
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="chart-widget"
      data-grid-id={id}
    >
      <div className="widget-header">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          {chartTitle && <Text strong style={{ fontSize: 18 }}>{chartTitle}</Text>}
          <WidgetInfoButton id={id} />
        </span>
      </div>
      <div className="chart-widget-body" style={{ position: "relative" }}>
        <HighchartsReact
          highcharts={Highcharts}
          options={options}
          ref={chartRef}
          containerProps={{ style: CHART_CONTAINER_STYLE }}
        />
        {!hasData && (
          <div style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}>
            <Text type="secondary" style={{ fontSize: 13 }}>
              No data found in the specified date range
            </Text>
          </div>
        )}
      </div>
    </div>
  );
}
